import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import * as yup from 'yup';
import type { CreativeEntry } from 'types/shared';
import type { CreatePinterestAdParams, PinterestCampaignWithAdGroups } from '../api';
import { buildCreatePinterestAdPayload } from '../utils/pinterestAdSubmitPayload';
import { createPinterestAdSchema } from '../validation/createPinterestAdSchema.schema';

interface UseCreatePinterestAdParamsReturn {
  params: CreatePinterestAdParams;
  isValid: boolean;
  errors: string[];
}

const EMPTY_ROWS: CreativeEntry[] = [];
const EMPTY_AD_GROUPS: PinterestCampaignWithAdGroups[] = [];
const EMPTY_IDS: string[] = [];

const useCreatePinterestAdParams = (): UseCreatePinterestAdParamsReturn => {
  useFormContext();
  const app = (useWatch({ name: 'app' }) as string | undefined) ?? '';
  const creativesRows = (useWatch({ name: '$creativesRows' }) as CreativeEntry[] | undefined) ?? EMPTY_ROWS;
  const adGroupsData =
    (useWatch({ name: '$adGroupsData' }) as PinterestCampaignWithAdGroups[] | undefined) ?? EMPTY_AD_GROUPS;
  const selectedAdGroups = (useWatch({ name: 'selectedAdGroups' }) as string[] | undefined) ?? EMPTY_IDS;

  const params = useMemo(
    () => buildCreatePinterestAdPayload({ app, creativesRows, adGroupsData, selectedAdGroups }),
    [app, creativesRows, adGroupsData, selectedAdGroups],
  );

  const validation = useMemo(() => {
    try {
      createPinterestAdSchema.validateSync(params, { abortEarly: false });
      return { isValid: true, errors: [] };
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        return { isValid: false, errors: error.errors };
      }
      return { isValid: false, errors: ['Unknown validation error occurred'] };
    }
  }, [params]);

  return { params, ...validation };
};

export default useCreatePinterestAdParams;
